import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import { putFileWithProgress, type UploadProgressHandler } from './fileUpload';

// ---------------------------------------------------------------------------
// Meeting Notes — polymorphic API client (CLE-205 / CLE-198 frontend).
//
// Wired to the unified backend router published by CLE-204:
//   /api/meeting-notes (CRUD + ingest/re-ingest/upload-url/sync-kb/pending-ingest/lint)
//
// Every note belongs to exactly one owner: clientId | projectId | leadId.
// Responses use the app-wide { success, data } envelope.
// ---------------------------------------------------------------------------

export const MEETING_TYPES = [
  'GENERAL',
  'STATUS',
  'KICKOFF',
  'DISCOVERY',
  'REVIEW',
  'RETROSPECTIVE',
  'PLANNING',
  'CALL',
  'EMAIL',
] as const;
export type MeetingType = (typeof MEETING_TYPES)[number];

export const INGEST_STATUSES = ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'] as const;
export type IngestStatus = (typeof INGEST_STATUSES)[number];

/** Exactly one of these identifies the owner of a note. */
export interface MeetingNoteOwner {
  clientId?: string;
  projectId?: string;
  leadId?: string;
  /** A team member (Person) owner — per-person 1:1 meeting notes (CLE-240). */
  personId?: string;
  /** A logged meeting (Meetings module) — its transcript + notes. */
  meetingId?: string;
}

export interface MeetingNote {
  id: string;
  clientId: string | null;
  projectId: string | null;
  leadId: string | null;
  personId: string | null;
  meetingId: string | null;
  title: string;
  date: string;
  attendees: string | null;
  meetingType: MeetingType | string;
  content: string;
  summary: string | null;
  decisions: string | null;
  actionItems: string | null;
  keyFacts: string | null;
  openQuestions: string | null;
  source: string;
  s3Key: string | null;
  s3Url: string | null;
  audioKey: string | null;
  ingestStatus: IngestStatus;
  ingestLog: string | null;
  lastIngestedAt: string | null;
  isIndexed: boolean;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MeetingNoteInput {
  title: string;
  content: string;
  date?: string;
  attendees?: string;
  meetingType?: MeetingType | string;
  summary?: string;
  decisions?: string;
  actionItems?: string;
  keyFacts?: string;
  openQuestions?: string;
}

export interface IngestMeetingNoteInput {
  text?: string;
  s3Key?: string;
  /** Uploaded audio recording key; transcribed (Whisper) during ingest. */
  audioKey?: string;
  title?: string;
  date?: string;
  meetingType?: MeetingType | string;
  /** When true the ingest runs synchronously and returns { note, ingest }. */
  sync?: boolean;
}

/** One semantic-search hit: the matched note plus a score and snippet. */
export interface MeetingNoteSearchHit {
  score: number;
  snippet: string;
  note: MeetingNote;
}

export interface UploadUrlResult {
  uploadUrl: string;
  key: string;
  expiresIn?: number;
}

export interface IngestRunResult {
  note: MeetingNote;
  ingest: {
    status: IngestStatus;
    log?: string | null;
    [key: string]: unknown;
  };
}

export interface SyncKbResult {
  id: string;
  kbPath: string;
  sourcePath: string;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// --- List / get ------------------------------------------------------------

export async function listMeetingNotes(
  owner: MeetingNoteOwner,
  filters: { ingestStatus?: IngestStatus } = {},
): Promise<MeetingNote[]> {
  const params = new URLSearchParams();
  if (owner.clientId) params.set('clientId', owner.clientId);
  if (owner.projectId) params.set('projectId', owner.projectId);
  if (owner.leadId) params.set('leadId', owner.leadId);
  if (owner.personId) params.set('personId', owner.personId);
  if (owner.meetingId) params.set('meetingId', owner.meetingId);
  if (filters.ingestStatus) params.set('ingestStatus', filters.ingestStatus);
  const qs = params.toString();
  const res = await apiFetch<Envelope<MeetingNote[]>>(`/api/meeting-notes${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(),
  });
  return res.data;
}

/**
 * Natural-language semantic search over meeting notes. Optionally scope to one
 * owner (e.g. a single person's 1:1 notes). Returns ranked hits with a snippet.
 */
export async function searchMeetingNotes(
  query: string,
  owner: MeetingNoteOwner = {},
  limit?: number,
): Promise<MeetingNoteSearchHit[]> {
  const params = new URLSearchParams({ q: query });
  if (owner.clientId) params.set('clientId', owner.clientId);
  if (owner.projectId) params.set('projectId', owner.projectId);
  if (owner.leadId) params.set('leadId', owner.leadId);
  if (owner.personId) params.set('personId', owner.personId);
  if (owner.meetingId) params.set('meetingId', owner.meetingId);
  if (limit) params.set('limit', String(limit));
  const res = await apiFetch<Envelope<{ query: string; results: MeetingNoteSearchHit[] }>>(
    `/api/meeting-notes/search?${params.toString()}`,
    { headers: authHeaders() },
  );
  return res.data.results;
}

export async function getMeetingNote(id: string): Promise<MeetingNote> {
  const res = await apiFetch<Envelope<MeetingNote>>(`/api/meeting-notes/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
}

// --- Create / update / delete ---------------------------------------------

export async function createMeetingNote(
  owner: MeetingNoteOwner,
  input: MeetingNoteInput,
): Promise<MeetingNote> {
  const res = await apiFetch<Envelope<MeetingNote>>('/api/meeting-notes', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ ...owner, ...input }),
  });
  return res.data;
}

export async function updateMeetingNote(
  id: string,
  input: Partial<MeetingNoteInput>,
): Promise<MeetingNote> {
  const res = await apiFetch<Envelope<MeetingNote>>(`/api/meeting-notes/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deleteMeetingNote(id: string): Promise<void> {
  await apiFetch<Envelope<{ id: string; deleted: boolean }>>(`/api/meeting-notes/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}

// --- Ingest ----------------------------------------------------------------

export async function ingestMeetingNote(
  owner: MeetingNoteOwner,
  input: IngestMeetingNoteInput,
): Promise<MeetingNote> {
  const res = await apiFetch<Envelope<MeetingNote | IngestRunResult>>('/api/meeting-notes/ingest', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ ...owner, ...input }),
  });
  // With sync:true the backend returns { note, ingest }; otherwise the Note.
  const data = res.data as MeetingNote | IngestRunResult;
  return 'note' in data ? data.note : data;
}

export async function reIngestMeetingNote(id: string): Promise<IngestRunResult> {
  const res = await apiFetch<Envelope<IngestRunResult>>(`/api/meeting-notes/${id}/re-ingest`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return res.data;
}

export async function requestMeetingNoteUploadUrl(
  owner: MeetingNoteOwner,
  input: { fileName: string; contentType?: string },
): Promise<UploadUrlResult> {
  const res = await apiFetch<Envelope<UploadUrlResult>>('/api/meeting-notes/upload-url', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ ...owner, contentType: 'application/pdf', ...input }),
  });
  return res.data;
}

export async function syncMeetingNoteToKb(id: string): Promise<SyncKbResult> {
  const res = await apiFetch<Envelope<SyncKbResult>>(`/api/meeting-notes/${id}/sync-kb`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return res.data;
}

// --- Shared: direct S3 upload ----------------------------------------------

// Upload raw bytes to a presigned S3 URL (not our API, so use raw fetch).
export async function uploadToSignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: UploadProgressHandler,
): Promise<void> {
  await putFileWithProgress(uploadUrl, file, contentType, onProgress);
}
